import React, { useEffect, useMemo, useState } from 'react';
import {
  AuditTrail, Callout, ConfirmDialog, Field, FormRow, FormSection, ImageDropzone, PhoneFrame, SaveBar,
  SummaryStrip, Toolbar, formatThaiDate, ghostBtnCls, inputCls, primaryBtnCls, secondaryBtnCls,
  useToast, type AuditEntry, type ImageValue, type SummaryStat,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  BANNERS, BANNER_ICONS, BANNER_STATUS_LABEL, BANNER_VISIBLE_LIMIT, TIERS, bannerStatus, todayInBangkok,
  type Banner, type BannerStatus, type TierId,
} from './loyaltyData';

/**
 * BannerScreen — แบนเนอร์แคมเปญบนหน้าแรกของแอป (FR-HOME-06)
 *
 * แบนเนอร์เป็นพื้นที่ที่ทุกฝ่ายอยากได้ และปัญหาที่เกิดจริงไม่ใช่การอัปโหลดภาพ
 * แต่คือ **ลำดับ** กับ **ใครเห็นอะไร** ซึ่งสองเรื่องนี้มองไม่เห็นจากตาราง
 *
 * จึงทำสองอย่าง:
 *
 *   1. **ลำดับเป็นรายการที่เลื่อนได้ ไม่ใช่ช่องกรอกเลข** — การกรอก "ลำดับ 3" ในตาราง
 *      ทำให้เกิดลำดับซ้ำและช่องว่างทันทีที่มีคนกรอกพร้อมกัน รายการที่เลื่อนขึ้นลง
 *      รับประกันว่าลำดับต่อเนื่องและไม่ซ้ำเสมอ
 *
 *   2. **พรีวิวมีตัวเลือก "ดูอย่างสมาชิกระดับ..."** — แบนเนอร์กำหนดกลุ่มผู้รับได้
 *      ถ้าพรีวิวแสดงทุกใบเสมอ คนตั้งค่าจะไม่เห็นว่าสมาชิก Silver เห็นแค่สองใบ
 *      และใบที่ตั้งใจให้เป็นใบแรกของ Silver จริง ๆ อยู่ลำดับสี่
 *
 * นอกจากนั้นหน้านี้เตือนเมื่อมีแบนเนอร์ที่แสดงอยู่เกิน 5 ใบ
 * เพราะจากอัตราการกดของใบท้าย ๆ ลูกค้าเลื่อนไม่ถึง — เพิ่มใบที่หกไม่ได้เพิ่มการมองเห็น
 * แต่ทำให้ใบที่หนึ่งถึงห้าถูกเห็นน้อยลง
 *
 * **สถานะไม่ใช่ค่าที่เลือกเอง** — เลือกได้แค่ "ฉบับร่าง / เผยแพร่"
 * ที่เหลือ (ตั้งเวลาไว้ · แสดงอยู่ · สิ้นสุดแล้ว) คำนวณจากวันที่ ด้วยกติกาเดียวกับ backend
 */

type ScreenState = 'ready' | 'loading' | 'error';

const STATUS_STYLE: Record<BannerStatus, { bg: string; fg: string; icon: string }> = {
  draft: { bg: 'var(--surface-100)', fg: 'var(--ink-700)', icon: 'edit_note' },
  scheduled: { bg: 'var(--info-bg)', fg: 'var(--info-text)', icon: 'schedule_send' },
  live: { bg: 'var(--success-bg)', fg: 'var(--success-text)', icon: 'play_circle' },
  ended: { bg: 'var(--surface-100)', fg: 'var(--ink-700)', icon: 'stop_circle' },
};

/** ตรงกับที่ backend ตรวจ — ภาพผิดข้อกำหนดจะถูกปฏิเสธตอนบันทึก */
const IMAGE_SPEC = {
  aspect: '16:9' as const,
  minWidth: 1_440,
  maxKB: 500,
  formats: ['JPG', 'PNG', 'WebP'],
  usedAt: 'แบนเนอร์เลื่อนบนหน้าแรกของแอป กว้างเต็มจอ 390px — ภาพถูกย่อลงทั้งใบ ไม่ครอป ป้ายมุม หัวข้อ และบรรทัดรองถูกวางทับด้านล่างของภาพ',
};

/** path ภายในแอป — กติกาเดียวกับ backend */
const APP_PATH_PATTERN = /^\/(?!\/)[A-Za-z0-9/\-_?=&]*$/;
/** ความยาวสูงสุดของปลายทาง — URL ของแคมเปญมักมี utm ยาว */
const LINK_MAX = 500;

/** ปลายทางเมื่อกด — ไม่ต้องเก็บแยก ดูจากตัวอักษรขึ้นต้นของ linkTo */
type LinkKind = 'none' | 'app' | 'external';
const linkKindOf = (v: string): LinkKind => {
  const t = v.trim();
  if (!t) return 'none';
  return t.startsWith('/') ? 'app' : 'external';
};

/** ชื่อโดเมนของลิงก์ภายนอก — แสดงให้เห็นชัดว่าจะพาลูกค้าไปที่ไหน */
const hostOf = (v: string): string | null => {
  try { return new URL(v.trim()).hostname; } catch { return null; }
};

/**
 * ตรวจลิงก์ภายนอก — กติกาเดียวกับที่ขอ backend (ภาคผนวก ก ของเอกสารขอ API แบนเนอร์)
 * รับ https ทุกโดเมน แต่ไม่รับ scheme อื่น ชื่อเครื่องในเครือข่าย หรือ URL ที่ฝังรหัสผ่าน
 */
function externalLinkProblem(v: string): string | null {
  const t = v.trim();
  if (/\s/.test(t)) return 'ลิงก์มีช่องว่างหรือตัวอักษรที่ใช้ไม่ได้';
  if (!/^https:\/\//i.test(t)) return 'ลิงก์ภายนอกต้องขึ้นต้นด้วย https://';
  let url: URL;
  try { url = new URL(t); } catch { return 'รูปแบบลิงก์ไม่ถูกต้อง'; }
  if (url.username || url.password) return 'ลิงก์ต้องไม่มีชื่อผู้ใช้หรือรหัสผ่าน';
  if (!url.hostname.includes('.') || /^[\d.]+$/.test(url.hostname)) return 'ลิงก์ภายนอกต้องเป็นชื่อโดเมนของเว็บไซต์ เช่น www.innobic.com';
  return null;
}

const ctr = (b: Banner) => (b.impressions === 0 ? null : (b.taps / b.impressions) * 100);

/** ระดับสมาชิกที่เลือกเป็นกลุ่มผู้รับได้ */
export interface BannerTierOption {
  id: TierId;
  name: string;
  memberCount?: number;
}

/** ช่องในหน้าต่างแก้ไขที่ผูกกับข้อผิดพลาด — ใช้แสดงข้อความใต้ช่องและพาไปที่ช่องแรกที่ผิด */
type BannerField = 'title' | 'name' | 'icon' | 'imageAlt' | 'from' | 'to' | 'link';

/** ลำดับเดียวกับช่องในหน้าต่างแก้ไข — ช่องแรกที่ผิดคือช่องที่ถูกพาไป */
const FIELD_ORDER: BannerField[] = ['title', 'name', 'imageAlt', 'icon', 'from', 'to', 'link'];

/** id ของช่องกรอก — ใช้โฟกัสช่องแรกที่ผิด */
const FIELD_INPUT_ID: Partial<Record<BannerField, string>> = {
  title: 'banner-title', name: 'banner-name', from: 'banner-from', to: 'banner-to', link: 'banner-link',
};

/**
 * ทุกจุดที่ทำให้บันทึกใบนี้ไม่ได้ — ตรวจแบบเดียวกับ backend เพื่อบอกก่อนกดบันทึก
 *
 * `linkKind` = ประเภทปลายทางที่เลือกในหน้าต่างแก้ไข (ถ้ามี) — เลือก "หน้าในแอป/เว็บไซต์ภายนอก"
 * แล้วปล่อยช่องว่างต้องถือว่ายังไม่ได้กรอก ไม่ใช่ "กดไม่ได้"
 */
function bannerProblems(b: Banner, linkKind?: LinkKind): Array<{ field: BannerField; message: string }> {
  const out: Array<{ field: BannerField; message: string }> = [];
  const add = (field: BannerField, message: string) => out.push({ field, message });

  if (!b.title.trim()) add('title', 'ยังไม่ได้กรอกหัวข้อที่ลูกค้าเห็น');
  if (!b.name.trim()) add('name', 'ยังไม่ได้ตั้งชื่ออ้างอิง');
  if (b.imageUrl && !b.imageAlt.trim()) add('imageAlt', 'ยังไม่ได้กรอกข้อความแทนภาพ');
  if (b.isPublished && !b.imageUrl && !b.icon) add('icon', 'แบนเนอร์ที่เผยแพร่ต้องมีภาพหรือไอคอน');
  if (b.isPublished && !b.from) add('from', 'ต้องระบุวันที่เริ่มแสดงก่อนเผยแพร่');
  if (b.from && b.to && b.to < b.from) add('to', 'วันสุดท้ายที่แสดงต้องไม่ก่อนวันเริ่มแสดง');

  const link = b.linkTo.trim();
  const kind = linkKind ?? linkKindOf(link);
  /* "https://" ที่เติมให้ตอนเลือกเว็บไซต์ภายนอก ยังไม่นับว่ากรอก */
  const blank = !link || /^https:\/\/$/i.test(link);
  if (kind !== 'none' && blank) {
    add('link', kind === 'external' ? 'ยังไม่ได้กรอกลิงก์เว็บไซต์' : 'ยังไม่ได้กรอกหน้าในแอป');
  } else if (link.length > LINK_MAX) {
    add('link', `ปลายทางยาวเกิน ${LINK_MAX.toLocaleString('th-TH')} ตัวอักษร`);
  } else if (linkKindOf(link) === 'app' && !APP_PATH_PATTERN.test(link)) {
    add('link', 'หน้าในแอปต้องขึ้นต้นด้วย / และใช้ได้เฉพาะ A-Z a-z 0-9 / - _ ? = &');
  } else if (linkKindOf(link) === 'external') {
    const p = externalLinkProblem(link);
    if (p) add('link', p);
  }
  return out.sort((x, y) => FIELD_ORDER.indexOf(x.field) - FIELD_ORDER.indexOf(y.field));
}

/** เหตุผลแรกที่ทำให้บันทึกใบนี้ไม่ได้ — ใช้ในรายการและแถบบันทึก */
const bannerProblem = (b: Banner): string | null => bannerProblems(b)[0]?.message ?? null;

export interface BannerScreenProps {
  initialState?: ScreenState;
  /**
   * แบนเนอร์จาก backend — ไม่ส่งมา = โหมดสาธิต ใช้ข้อมูลจำลองและบันทึกแค่ในหน้า
   */
  banners?: Banner[];
  /** ระดับสมาชิกจริง — ไม่ส่งมา = ใช้ระดับตัวอย่าง */
  tiers?: BannerTierOption[];
  /**
   * บันทึกทั้งชุด — ส่งรายการที่แก้แล้วและรายการก่อนแก้ให้ชั้นข้อมูลหาเองว่าต้องสร้าง/แก้/ลบ/จัดลำดับอะไร
   * throw = บันทึกไม่สำเร็จ ข้อความของ error ถูกแสดงตรง ๆ
   */
  /** `reason` = เหตุผลจากกล่องยืนยัน — backend เก็บใน Audit Log ของทุกคำขอในรอบบันทึกนั้น */
  onSave?: (next: Banner[], prev: Banner[], reason?: string) => Promise<void>;
  /** ประวัติการแก้ไข — ไม่ส่งมา = ไม่แสดงส่วนนี้ */
  auditEntries?: AuditEntry[];
}

export const BannerScreen: React.FC<BannerScreenProps> = ({
  initialState = 'ready', banners, tiers: tiersProp, onSave, auditEntries,
}) => {
  const toast = useToast();
  const live = banners !== undefined;
  const tiers: BannerTierOption[] = useMemo(
    () => tiersProp ?? TIERS.map((t) => ({ id: t.id, name: t.name, memberCount: t.memberCount })),
    [tiersProp],
  );
  const tierName = (id: TierId) => tiers.find((t) => t.id === id)?.name ?? `ระดับ #${id}`;

  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [saved, setSaved] = useState<Banner[]>(banners ?? BANNERS);
  const [draft, setDraft] = useState<Banner[]>(banners ?? BANNERS);
  const [edit, setEdit] = useState<Banner | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewAs, setPreviewAs] = useState<'all' | TierId>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | BannerStatus>('all');
  /**
   * ประเภทปลายทางที่เลือกในหน้าต่างแก้ไข — เก็บแยกจาก linkTo
   * เพราะตอนเพิ่งเลือก "เว็บไซต์ภายนอก" ช่องยังว่าง ถ้าดูจาก linkTo อย่างเดียวจะเด้งกลับเป็น "กดไม่ได้"
   */
  const [linkKind, setLinkKind] = useState<LinkKind>('none');
  /** กด "เสร็จแล้ว" ไปแล้วแต่ยังมีจุดผิด — เริ่มแสดงข้อความใต้ช่อง (ก่อนหน้านั้นไม่ดุระหว่างพิมพ์) */
  const [showErrors, setShowErrors] = useState(false);

  /* ข้อมูลจริงมาทีหลัง และมาใหม่ทุกครั้งที่บันทึกเสร็จ — รับค่าใหม่ทั้งสองฝั่ง */
  useEffect(() => {
    if (!banners) return;
    setSaved(banners);
    setDraft(banners);
  }, [banners]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);

  const today = todayInBangkok();
  const statusOf = (b: Banner) => bannerStatus(b, today);

  const audienceText = (b: Banner): string => {
    const parts: string[] = [];
    if (b.audience.tiers.length === 0) parts.push('สมาชิกทุกระดับ');
    else parts.push(b.audience.tiers.map(tierName).join(' · '));
    /* ข้อความอิสระมีแค่ในงานออกแบบเดิม — backend ไม่มีฟิลด์นี้ */
    if (!live && b.audience.segment) parts.push(b.audience.segment);
    return parts.join(' · ');
  };

  const rangeText = (b: Banner) =>
    `${b.from ? formatThaiDate(b.from) : 'ยังไม่กำหนดวันเริ่ม'} – ${b.to ? formatThaiDate(b.to) : 'ไม่มีกำหนดสิ้นสุด'}`;

  const ordered = useMemo(() => [...draft].sort((a, b) => a.order - b.order), [draft]);
  const shown = ordered.filter((b) => statusFilter === 'all' || statusOf(b) === statusFilter);

  const move = (id: string, dir: -1 | 1) => {
    const list = [...ordered];
    const i = list.findIndex((b) => b.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j]!, list[i]!];
    setDraft(list.map((b, k) => ({ ...b, order: k + 1 })));
  };

  const patch = (id: string, next: Partial<Banner>) =>
    setDraft((bs) => bs.map((b) => (b.id === id ? { ...b, ...next } : b)));

  /** แก้ใบที่เปิดอยู่ในหน้าต่างแก้ไข — เขียนทั้งรายการร่างและค่าในหน้าต่าง */
  /** เปิดหน้าต่างแก้ไข — ตั้งประเภทปลายทางตามค่าของใบนั้น */
  const openEdit = (b: Banner) => {
    setLinkKind(linkKindOf(b.linkTo));
    setShowErrors(false);
    setEdit(b);
  };

  const editField = (next: Partial<Banner>) => {
    if (!edit) return;
    patch(edit.id, next);
    setEdit({ ...edit, ...next });
  };

  /* --------------------------------------------------------- พรีวิวหน้าแรก */

  const previewList = ordered.filter((b) => {
    if (statusOf(b) !== 'live') return false;
    if (previewAs === 'all') return true;
    return b.audience.tiers.length === 0 || b.audience.tiers.includes(previewAs);
  });

  const liveCount = ordered.filter((b) => statusOf(b) === 'live').length;
  const overLimit = liveCount > BANNER_VISIBLE_LIMIT;
  const iconOnly = draft.filter((b) => (statusOf(b) === 'live' || statusOf(b) === 'scheduled') && !b.imageUrl).length;
  const problem = useMemo(() => {
    for (const b of ordered) {
      const p = bannerProblem(b);
      if (p) return `${b.name || b.title || 'แบนเนอร์ใหม่'} — ${p}`;
    }
    return null;
  }, [ordered]);

  /* ------------------------------------------------------- สิ่งที่เปลี่ยนไป */

  const changes = useMemo(() => {
    const out: Array<{ field: string; from: string; to: string }> = [];
    const pubText = (b: Banner) => (b.isPublished ? 'เผยแพร่' : 'ฉบับร่าง');
    draft.forEach((b) => {
      const s = saved.find((x) => x.id === b.id);
      const label = b.name || b.title || 'แบนเนอร์ใหม่';
      if (!s) { out.push({ field: `แบนเนอร์ใหม่ — ${label}`, from: 'ไม่มี', to: `ลำดับ ${b.order}` }); return; }
      if (s.order !== b.order) out.push({ field: `ลำดับ — ${label}`, from: `${s.order}`, to: `${b.order}` });
      if (s.isPublished !== b.isPublished) out.push({ field: `การเผยแพร่ — ${label}`, from: pubText(s), to: pubText(b) });
      if (s.name !== b.name) out.push({ field: 'ชื่ออ้างอิง', from: s.name, to: b.name });
      if (s.title !== b.title) out.push({ field: `หัวข้อ — ${label}`, from: s.title, to: b.title });
      if (s.tag !== b.tag) out.push({ field: `ป้ายมุม — ${label}`, from: s.tag || 'ไม่มี', to: b.tag || 'ไม่มี' });
      if (s.caption !== b.caption) out.push({ field: `บรรทัดรอง — ${label}`, from: s.caption || 'ไม่มี', to: b.caption || 'ไม่มี' });
      if (s.icon !== b.icon) out.push({ field: `ไอคอนสำรอง — ${label}`, from: s.icon || 'ไม่มี', to: b.icon || 'ไม่มี' });
      if (s.from !== b.from || s.to !== b.to) out.push({ field: `ช่วงเวลา — ${label}`, from: rangeText(s), to: rangeText(b) });
      if (audienceText(s) !== audienceText(b)) out.push({ field: `กลุ่มผู้รับ — ${label}`, from: audienceText(s), to: audienceText(b) });
      if (s.imageUrl !== b.imageUrl) out.push({ field: `ภาพ — ${label}`, from: s.imageUrl ? 'ภาพเดิม' : 'ไม่มีภาพ', to: b.imageUrl ? 'ภาพใหม่' : 'เอาออก' });
      if (s.imageAlt !== b.imageAlt && s.imageUrl === b.imageUrl) out.push({ field: `ข้อความแทนภาพ — ${label}`, from: s.imageAlt, to: b.imageAlt });
      if (s.linkTo !== b.linkTo) out.push({ field: `ปลายทางเมื่อกด — ${label}`, from: s.linkTo || 'กดไม่ได้', to: b.linkTo || 'กดไม่ได้' });
    });
    saved.forEach((s) => {
      if (!draft.some((b) => b.id === s.id)) out.push({ field: `ลบแบนเนอร์ — ${s.name || s.title}`, from: `ลำดับ ${s.order}`, to: 'ลบ' });
    });
    return out;
  }, [draft, saved]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSave = async (reason?: string) => {
    if (!onSave) {
      /* โหมดสาธิต — บันทึกแค่ในหน้า */
      const before = saved;
      setSaved(draft);
      setConfirmOpen(false);
      toast.show({
        tone: 'success',
        message: 'บันทึกแบนเนอร์แล้ว — ลำดับใหม่ขึ้นบนหน้าแรกของแอปทันที',
        undo: { onUndo: () => { setSaved(before); setDraft(before); }, seconds: 10 },
      });
      return;
    }
    setSaving(true);
    try {
      await onSave(ordered, saved, reason);
      setConfirmOpen(false);
      toast.show({ tone: 'success', message: 'บันทึกแบนเนอร์แล้ว — ลำดับใหม่ขึ้นบนหน้าแรกของแอปทันที' });
    } catch (err) {
      setConfirmOpen(false);
      toast.show({ tone: 'error', message: err instanceof Error ? err.message : 'บันทึกแบนเนอร์ไม่สำเร็จ' });
    } finally {
      setSaving(false);
    }
  };

  const stats: SummaryStat[] = [
    { label: 'แสดงอยู่บนหน้าแรก', value: liveCount.toLocaleString('th-TH') + ' ใบ', icon: 'view_carousel', tone: overLimit ? 'warning' : 'brand', note: overLimit ? `เกิน ${BANNER_VISIBLE_LIMIT} ใบ` : undefined },
    { label: 'ตั้งเวลาไว้', value: ordered.filter((b) => statusOf(b) === 'scheduled').length.toLocaleString('th-TH') + ' ใบ', icon: 'schedule_send' },
    { label: 'การมองเห็นรวม', value: ordered.reduce((s, b) => s + b.impressions, 0).toLocaleString('th-TH'), icon: 'visibility' },
    { label: 'การกดรวม', value: ordered.reduce((s, b) => s + b.taps, 0).toLocaleString('th-TH'), icon: 'touch_app', tone: 'success' },
  ];

  if (screenState === 'error') {
    return (
      <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
        <Toolbar title="แบนเนอร์หน้าแรก" />
        <div className="p-[var(--space-4)]">
          <Callout tone="error" title="โหลดรายการแบนเนอร์ไม่สำเร็จ">เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ลองใหม่อีกครั้ง</Callout>
        </div>
      </div>
    );
  }

  /* ใบที่เปิดแก้อยู่ — ลบไม่ได้ถ้ายังแสดงอยู่ (backend ปฏิเสธเหมือนกัน) */
  const editStatus = edit ? statusOf(edit) : null;
  const editIsNew = edit ? !saved.some((x) => x.id === edit.id) : false;
  const editProblems = edit ? bannerProblems(edit, linkKind) : [];
  /** ข้อความใต้ช่อง — แสดงหลังกด "เสร็จแล้ว" ครั้งแรกเท่านั้น */
  const fieldError = (f: BannerField): string | undefined =>
    (showErrors ? editProblems.find((p) => p.field === f)?.message : undefined);

  /**
   * ปิดหน้าต่างแก้ไข — ตรวจทุกช่องก่อน ผิดตรงไหนไม่ปิด และพาไปที่ช่องแรกที่ผิด
   * ให้ผู้ใช้รู้ตอนนี้ ไม่ใช่ไปรู้ตอนกดบันทึกทั้งหน้าแล้วต้องหาเองว่าใบไหน
   */
  const finishEdit = () => {
    if (!edit) return;
    const problems = bannerProblems(edit, linkKind);
    if (problems.length === 0) { setEdit(null); return; }
    setShowErrors(true);
    const first = problems[0]!.field;
    /* รอให้ข้อความใต้ช่องแสดงก่อน แล้วค่อยเลื่อนไปและโฟกัส */
    window.setTimeout(() => {
      const dlg = document.querySelector<HTMLElement>('[data-banner-editor]');
      const id = FIELD_INPUT_ID[first];
      const target = id
        ? document.getElementById(id)
        : first === 'imageAlt'
          ? dlg?.querySelector<HTMLElement>('[data-field="imageAlt"] input[type="text"]')
          : dlg?.querySelector<HTMLElement>('[data-field="icon"] input');
      target?.scrollIntoView({ block: 'center' });
      target?.focus();
    }, 0);
  };

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="แบนเนอร์หน้าแรก"
        count={ordered.length}
        end={
          <>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => {
                const b: Banner = {
                  id: `bn-${Date.now()}`, name: '', title: '', caption: '', tag: '', icon: BANNER_ICONS[0]!.name,
                  order: ordered.length + 1, imageUrl: null, imageAlt: '', from: today, to: null,
                  isPublished: false, audience: { tiers: [] }, linkTo: '', impressions: 0, taps: 0,
                };
                setDraft((prev) => [...prev, b]);
                openEdit(b);
              }}
            >
              <Icon name="add" size={17} />
              เพิ่มแบนเนอร์
            </button>
            <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
              สถานะ
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className={inputCls}>
                <option value="all">ทั้งหมด</option>
                {(Object.keys(BANNER_STATUS_LABEL) as BannerStatus[]).map((s) => (
                  <option key={s} value={s}>{BANNER_STATUS_LABEL[s]}</option>
                ))}
              </select>
            </label>
          </>
        }
      />

      <SummaryStrip stats={stats} />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 gap-[var(--space-4)] overflow-y-auto p-[var(--space-4)]">
          <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-3)]">
            {overLimit && (
              <Callout tone="warning" title={`มีแบนเนอร์แสดงอยู่ ${liveCount} ใบ`}>
                จากอัตราการกดของใบท้าย ๆ ลูกค้าส่วนใหญ่เลื่อนไม่ถึงใบที่ {BANNER_VISIBLE_LIMIT + 1} —
                การเพิ่มใบใหม่ไม่ได้เพิ่มการมองเห็นรวม แต่ทำให้ใบที่ 1–{BANNER_VISIBLE_LIMIT} ถูกเห็นน้อยลง
                ถ้าต้องขึ้นใบใหม่ ให้หยุดใบที่กดน้อยที่สุดก่อน
              </Callout>
            )}
            {iconOnly > 0 && (
              <Callout tone="info" title={`${iconOnly} ใบยังไม่มีภาพ`}>
                ลูกค้าจะเห็นไอคอนสำรองบนพื้นสีแทนภาพ — อัปโหลดภาพ 16:9 เพื่อให้การ์ดเด่นขึ้น
              </Callout>
            )}

            <FormSection
              title="ลำดับการแสดงบนหน้าแรก"
              requirement="FR-HOME-06"
              description="ใบที่ 1 คือใบที่ลูกค้าเห็นก่อนโดยไม่ต้องเลื่อน — เลื่อนขึ้นลงเพื่อจัดลำดับ ลำดับจะต่อเนื่องเสมอ"
            >
              <ul className="m-0 flex list-none flex-col gap-[var(--space-2)] p-0">
                {shown.map((b) => {
                  const rate = ctr(b);
                  const status = statusOf(b);
                  const st = STATUS_STYLE[status];
                  const i = ordered.findIndex((x) => x.id === b.id);
                  const belowFold = status === 'live' && i + 1 > BANNER_VISIBLE_LIMIT;
                  const label = b.name || b.title || 'แบนเนอร์ใหม่';
                  const rowProblem = bannerProblem(b);
                  return (
                    <li
                      key={b.id}
                      className="flex items-start gap-[var(--space-3)] rounded-[var(--radius-md)] border bg-[var(--surface-0)] p-[var(--space-25)]"
                      style={{ borderColor: rowProblem ? 'var(--error-border)' : belowFold ? 'var(--warning-border)' : 'var(--line-200)' }}
                    >
                      <span className="flex shrink-0 flex-col items-center gap-[var(--space-05)]">
                        <button
                          type="button"
                          aria-label={`เลื่อน ${label} ขึ้น`}
                          disabled={i === 0}
                          onClick={() => move(b.id, -1)}
                          className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] disabled:text-[var(--ink-400)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        >
                          <Icon name="arrow_upward" size={17} />
                        </button>
                        <span className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'center' }}>
                          {b.order}
                        </span>
                        <button
                          type="button"
                          aria-label={`เลื่อน ${label} ลง`}
                          disabled={i === ordered.length - 1}
                          onClick={() => move(b.id, 1)}
                          className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] disabled:text-[var(--ink-400)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        >
                          <Icon name="arrow_downward" size={17} />
                        </button>
                      </span>

                      {b.imageUrl ? (
                        <img src={b.imageUrl} alt="" className="h-[63px] w-[112px] shrink-0 rounded-[var(--radius-sm)] object-cover" />
                      ) : (
                        <span className="flex h-[63px] w-[112px] shrink-0 flex-col items-center justify-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-dashed border-[var(--line-300)] bg-[var(--surface-100)]">
                          <Icon name={b.icon || 'add_photo_alternate'} size={19} className="text-[var(--ink-600)]" />
                          <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ยังไม่มีภาพ</span>
                        </span>
                      )}

                      <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-05)]">
                        <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                          <span className="bo-truncate text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                            {b.title || '(ยังไม่ได้กรอกหัวข้อ)'}
                          </span>
                          <span
                            className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)]"
                            style={{ background: st.bg, color: st.fg }}
                          >
                            <Icon name={st.icon} size={14} filled />
                            {BANNER_STATUS_LABEL[status]}
                          </span>
                          {belowFold && (
                            <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
                              <Icon name="visibility_off" size={14} filled />
                              ลูกค้าส่วนใหญ่เลื่อนไม่ถึง
                            </span>
                          )}
                        </span>
                        {b.name && b.name !== b.title && (
                          <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">ชื่ออ้างอิง: {b.name}</span>
                        )}
                        <span className="flex flex-wrap items-center gap-x-[var(--space-3)] text-[length:var(--text-table)] text-[var(--ink-700)]">
                          <span className="flex items-center gap-1">
                            <Icon name="event" size={15} />
                            <span className="bo-num" style={{ textAlign: 'left' }}>{rangeText(b)}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="groups" size={15} />
                            {audienceText(b)}
                          </span>
                          {linkKindOf(b.linkTo) === 'external' ? (
                            <span className="flex items-center gap-1" title={b.linkTo}>
                              <Icon name="open_in_new" size={15} />
                              <span className="bo-truncate max-w-[220px]">เว็บภายนอก · {hostOf(b.linkTo) ?? b.linkTo}</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Icon name="link" size={15} />
                              <span className="bo-truncate max-w-[180px]">{b.linkTo || 'กดไม่ได้'}</span>
                            </span>
                          )}
                        </span>
                        {b.impressions > 0 && (
                          <span className="flex flex-wrap items-center gap-x-[var(--space-3)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                            <span>เห็น <strong className="bo-num">{b.impressions.toLocaleString('th-TH')}</strong> ครั้ง</span>
                            <span>กด <strong className="bo-num">{b.taps.toLocaleString('th-TH')}</strong> ครั้ง</span>
                            {rate !== null && (
                              <span style={{ color: rate < 3 ? 'var(--warning-text)' : 'var(--success-text)' }}>
                                อัตราการกด <strong className="bo-num">{rate.toFixed(1)}%</strong>
                                {rate < 3 ? ' — ต่ำกว่าค่าเฉลี่ย' : ''}
                              </span>
                            )}
                          </span>
                        )}
                        {rowProblem && (
                          <span className="flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
                            <Icon name="error" size={14} filled />
                            {rowProblem}
                          </span>
                        )}
                      </div>

                      <span className="flex shrink-0 items-center gap-[var(--space-1)]">
                        <select
                          value={b.isPublished ? 'published' : 'draft'}
                          onChange={(e) => patch(b.id, { isPublished: e.target.value === 'published' })}
                          className={inputCls}
                          aria-label={`การเผยแพร่ของ ${label}`}
                        >
                          <option value="draft">ฉบับร่าง</option>
                          <option value="published">เผยแพร่</option>
                        </select>
                        <button type="button" onClick={() => openEdit(b)} className={secondaryBtnCls}>แก้ไข</button>
                      </span>
                    </li>
                  );
                })}
              </ul>

              {shown.length === 0 && (
                <p className="m-0 py-[var(--space-4)] text-center text-[length:var(--text-form)] text-[var(--ink-600)]">
                  {ordered.length === 0 ? 'ยังไม่มีแบนเนอร์ — กด "เพิ่มแบนเนอร์" เพื่อเริ่ม' : 'ไม่มีแบนเนอร์ในสถานะนี้'}
                </p>
              )}
            </FormSection>

            <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
              <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
              ตัวเลขการมองเห็นและการกดเป็นยอดสะสมตั้งแต่เริ่มแสดง — นับการเห็นไม่เกินวันละครั้งต่อสมาชิก
              ส่วนการกดนับทุกครั้ง
            </p>

            {auditEntries && (
              <FormSection title="ประวัติการแก้ไข" description="ทุกการสร้าง แก้ไข จัดลำดับ และลบแบนเนอร์">
                <AuditTrail entries={auditEntries} />
              </FormSection>
            )}
            <span className="pb-[var(--space-4)]" />
          </div>

          <aside className="sticky top-0 flex shrink-0 flex-col gap-[var(--space-3)] self-start">
            <div className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-25)]">
              <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)]">
                <Icon name="visibility" size={17} className="text-[var(--blue-700)]" />
                ดูอย่างสมาชิก
                <select value={previewAs} onChange={(e) => setPreviewAs(e.target.value as typeof previewAs)} className={inputCls}>
                  <option value="all">ไม่กรองกลุ่ม (เห็นทุกใบ)</option>
                  {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                {previewAs === 'all'
                  ? `แสดงแบนเนอร์ที่สถานะ "แสดงอยู่" ทั้ง ${previewList.length} ใบ`
                  : `สมาชิก ${tierName(previewAs)} เห็น ${previewList.length} ใบ จากทั้งหมด ${liveCount} ใบที่แสดงอยู่`}
              </span>
            </div>

            <PhoneFrame
              caption="พรีวิวหน้าแรกของแอป — แบนเนอร์เลื่อนแนวนอน ใบแรกคือใบที่เห็นก่อนโดยไม่ต้องเลื่อน"
              height={560}
            >
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)' }}>สวัสดีคุณสมาชิก</span>
                  <span style={{ fontSize: 16, lineHeight: 1.6, fontWeight: 600, color: 'var(--ink-900)' }}>
                    {previewAs === 'all' ? 'สมาชิกทุกระดับ' : `สมาชิก ${tierName(previewAs)}`}
                  </span>
                </div>

                {previewList.length === 0 ? (
                  <div
                    style={{
                      border: '1px dashed var(--line-300)', borderRadius: 12, padding: 24,
                      textAlign: 'center', fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)',
                    }}
                  >
                    ไม่มีแบนเนอร์ที่กลุ่มนี้เห็น — พื้นที่นี้จะหายไปจากหน้าแรก
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
                    {previewList.map((b) => (
                      <div
                        key={b.id}
                        style={{
                          width: 300, flexShrink: 0, borderRadius: 12, overflow: 'hidden',
                          border: '1px solid var(--line-200)', background: 'var(--surface-0)',
                        }}
                      >
                        <div style={{ position: 'relative' }}>
                          {b.imageUrl ? (
                            <img
                              src={b.imageUrl}
                              alt={b.imageAlt || b.title}
                              style={{ width: 300, aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 300, aspectRatio: '16 / 9', background: 'var(--blue-050)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              <Icon name={b.icon || 'redeem'} size={48} className="text-[var(--blue-700)]" />
                            </div>
                          )}
                          {b.tag && (
                            /* ป้ายพื้นทึบเหมือนในแอป — ห้ามวางตัวอักษรบนภาพตรง ๆ เพราะ contrast ไม่ผ่าน */
                            <span
                              style={{
                                position: 'absolute', left: 8, top: 8, borderRadius: 6, padding: '2px 10px',
                                background: 'var(--lime-400)', color: 'var(--blue-700)', fontSize: 14, fontWeight: 600,
                              }}
                            >
                              {b.tag}
                            </span>
                          )}
                        </div>
                        <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: 16, lineHeight: 1.5, fontWeight: 600, color: 'var(--ink-900)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {b.title || '(ยังไม่ได้กรอกหัวข้อ)'}
                            {/* บอกลูกค้าว่ากดแล้วจะออกนอกแอป — แอปลูกค้าต้องแสดงแบบเดียวกัน */}
                            {linkKindOf(b.linkTo) === 'external' && (
                              <Icon name="open_in_new" size={16} className="shrink-0 text-[var(--ink-700)]" label="เปิดนอกแอป" />
                            )}
                          </span>
                          {b.caption && (
                            <span style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-700)' }}>{b.caption}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  {['แลกของรางวัล', 'สิทธิ์ของฉัน'].map((text) => (
                    <div
                      key={text}
                      style={{
                        flex: 1, minHeight: 44, borderRadius: 12, background: 'var(--surface-100)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)',
                      }}
                    >
                      {text}
                    </div>
                  ))}
                </div>
              </div>
            </PhoneFrame>
          </aside>
        </div>

        <SaveBar
          changeCount={changes.length}
          changes={changes}
          onSave={() => setConfirmOpen(true)}
          onDiscard={() => setDraft(saved)}
          blockedReason={problem ?? undefined}
          busy={saving}
          impact={<span>ลำดับและสถานะใหม่ขึ้นบนหน้าแรกของแอปทันทีที่บันทึก — ไม่ต้องรอรอบปล่อยเวอร์ชัน</span>}
        />
      </div>

      {/* --------------------------------------------------------- แก้ไขแบนเนอร์ */}
      <div>
        {edit && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`แก้ไขแบนเนอร์ ${edit.name || edit.title || 'ใหม่'}`}
            className="fixed inset-0 z-[var(--z-drawer)] flex items-start justify-center overflow-y-auto p-[var(--space-6)]"
            style={{ background: 'var(--scrim)' }}
          >
            <div
              data-banner-editor
              className="flex w-full max-w-[880px] flex-col gap-[var(--space-4)] rounded-[var(--radius-lg)] bg-[var(--surface-0)] p-[var(--space-5)]"
              style={{ boxShadow: 'var(--elevation-modal)' }}
            >
              <header className="flex items-start justify-between gap-[var(--space-3)]">
                <div className="flex flex-col gap-[var(--space-05)]">
                  <h2 className="m-0 text-[length:var(--text-page-title)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                    {editIsNew ? 'แบนเนอร์ใหม่' : 'แก้ไขแบนเนอร์'}
                  </h2>
                  <p className="m-0 text-[length:var(--text-form)] text-[var(--ink-700)]">ลำดับที่ {edit.order} จาก {ordered.length} ใบ</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    /* กดเพิ่มแล้วเปลี่ยนใจ — ใบเปล่าที่ไม่ได้กรอกอะไรไม่ต้องค้างไว้ให้บันทึกทั้งหน้าไม่ได้ */
                    if (editIsNew && !edit.title.trim() && !edit.name.trim()) {
                      setDraft((prev) => prev.filter((x) => x.id !== edit.id).map((b, k) => ({ ...b, order: k + 1 })));
                    }
                    setEdit(null);
                  }}
                  aria-label="ปิด"
                  className="inline-flex min-h-[var(--hit-control)] min-w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                >
                  <Icon name="close" size={20} />
                </button>
              </header>

              <FormRow label="หัวข้อที่ลูกค้าเห็น" required htmlFor="banner-title" help="ตัวหนาใต้ภาพบนการ์ด — ไม่เกิน 80 ตัวอักษร" error={fieldError('title')}>
                <input
                  id="banner-title"
                  data-autofocus
                  type="text"
                  maxLength={80}
                  value={edit.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    /* ชื่ออ้างอิงตามหัวข้อไปก่อน จนกว่าผู้ใช้จะแก้ชื่ออ้างอิงเอง */
                    const followsTitle = !edit.name || edit.name === edit.title.slice(0, 120);
                    editField(followsTitle ? { title, name: title.slice(0, 120) } : { title });
                  }}
                  className={`${inputCls} w-full`}
                  placeholder="เช่น แลกคะแนนรับคูปองส่วนลด 300 บาท"
                />
              </FormRow>

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow label="ป้ายมุม" htmlFor="banner-tag" help="ป้ายเล็กมุมซ้ายบนของภาพ — เว้นว่างได้ ไม่เกิน 30 ตัวอักษร">
                  <input
                    id="banner-tag"
                    type="text"
                    maxLength={30}
                    value={edit.tag}
                    onChange={(e) => editField({ tag: e.target.value })}
                    className={`${inputCls} w-full`}
                    placeholder="เช่น เฉพาะสมาชิก Gold"
                  />
                </FormRow>
                <FormRow label="บรรทัดรอง" htmlFor="banner-caption" help="ใต้หัวข้อ — เว้นว่างได้ ไม่เกิน 80 ตัวอักษร">
                  <input
                    id="banner-caption"
                    type="text"
                    maxLength={80}
                    value={edit.caption}
                    onChange={(e) => editField({ caption: e.target.value })}
                    className={`${inputCls} w-full`}
                    placeholder="เช่น ถึง 31 ต.ค. 2569"
                  />
                </FormRow>
              </div>

              <FormRow label="ชื่ออ้างอิง" required htmlFor="banner-name" help="ใช้ในรายงานและประวัติการแก้ไข ลูกค้าไม่เห็น — ตั้งตามหัวข้อให้อัตโนมัติจนกว่าจะแก้เอง" error={fieldError('name')}>
                <input
                  id="banner-name"
                  type="text"
                  maxLength={120}
                  value={edit.name}
                  onChange={(e) => editField({ name: e.target.value })}
                  className={`${inputCls} w-full`}
                  placeholder="เช่น ต.ค. 69 · คูปอง 300 บาท"
                />
              </FormRow>

              {/* ข้อความแทนภาพที่ยังไม่กรอก กล่องอัปโหลดเตือนใต้ช่องเองอยู่แล้ว — ห่อไว้ให้หาช่องเจอตอนพาไปช่องที่ผิด */}
              <div data-field="imageAlt">
              <ImageDropzone
                /* ⚠️ ต้องส่ง `file` กลับเข้าไปด้วย — ไม่งั้นไฟล์หายตอนแก้ข้อความแทนภาพ */
                value={edit.imageUrl ? { url: edit.imageUrl, alt: edit.imageAlt, file: edit.imageFile } : null}
                onChange={(v: ImageValue | null) => editField({
                  imageUrl: v?.url ?? null,
                  imageAlt: v?.alt ?? '',
                  imageFile: v?.file,
                })}
                spec={IMAGE_SPEC}
                label="ภาพแบนเนอร์ 16:9"
                previewWidth={400}
                /* backend ปฏิเสธภาพที่ไม่ตรงข้อกำหนด — ปรับให้ตั้งแต่ตอนเลือก ไม่ให้ไปล้มตอนบันทึก */
                autoFit
              />
              </div>

              <FormRow label="ไอคอนสำรองเมื่อไม่มีภาพ" help="แสดงบนพื้นสีแทนภาพ — เลือกได้เฉพาะไอคอนที่แอปลูกค้าแสดงได้" error={fieldError('icon')}>
                <span data-field="icon" className="flex flex-wrap gap-[var(--space-2)]" role="radiogroup" aria-label="ไอคอนสำรอง">
                  {BANNER_ICONS.map((ic) => {
                    const on = edit.icon === ic.name;
                    return (
                      <label
                        key={ic.name}
                        className={`flex cursor-pointer items-center gap-[var(--space-15)] rounded-[var(--radius-pill)] border px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-form)] ${
                          on ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]' : 'border-[var(--line-300)] text-[var(--ink-700)]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="banner-icon"
                          checked={on}
                          onChange={() => editField({ icon: ic.name })}
                          className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        />
                        <Icon name={ic.name} size={18} />
                        {ic.label}
                      </label>
                    );
                  })}
                </span>
              </FormRow>

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow label="เริ่มแสดง" required={edit.isPublished} htmlFor="banner-from" error={fieldError('from')}>
                  <input
                    id="banner-from"
                    type="date"
                    value={edit.from}
                    onChange={(e) => editField({ from: e.target.value })}
                    className={`${inputCls} bo-num`}
                  />
                </FormRow>
                <FormRow label="แสดงถึง" htmlFor="banner-to" help="เว้นว่าง = แสดงไปเรื่อย ๆ จนกว่าจะหยุดเอง" error={fieldError('to')}>
                  <input
                    id="banner-to"
                    type="date"
                    value={edit.to ?? ''}
                    onChange={(e) => editField({ to: e.target.value || null })}
                    className={`${inputCls} bo-num`}
                  />
                </FormRow>
              </div>

              <FormRow label="กลุ่มผู้รับ" help="ไม่เลือกเลย = สมาชิกทุกระดับเห็นแบนเนอร์นี้">
                <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                  {tiers.map((t) => {
                    const on = edit.audience.tiers.includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className={`flex cursor-pointer items-center gap-[var(--space-15)] rounded-[var(--radius-pill)] border px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-form)] ${
                          on ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]' : 'border-[var(--line-300)] text-[var(--ink-700)]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...edit.audience.tiers, t.id]
                              : edit.audience.tiers.filter((x) => x !== t.id);
                            editField({ audience: { ...edit.audience, tiers: next } });
                          }}
                          className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        />
                        {t.name}
                        {t.memberCount !== undefined && (
                          <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{t.memberCount.toLocaleString('th-TH')} คน</span>
                        )}
                      </label>
                    );
                  })}
                </span>
              </FormRow>

              {!live && (
                <FormRow
                  label="เงื่อนไขเพิ่มเติมของกลุ่มผู้รับ"
                  help="ระบุเป็นข้อความในรอบนี้ — การเลือกกลุ่มผู้รับแบบละเอียดจะเปิดในเฟสถัดไป"
                >
                  <input
                    type="text"
                    value={edit.audience.segment ?? ''}
                    onChange={(e) => editField({ audience: { ...edit.audience, segment: e.target.value || undefined } })}
                    className={`${inputCls} w-full`}
                    placeholder="เช่น ยังไม่เคยสั่งซื้อ · กลุ่ม ปตท."
                  />
                </FormRow>
              )}

<FormRow label="ปลายทางเมื่อกด" htmlFor="banner-link" error={fieldError('link')}>
                <span className="flex flex-col gap-[var(--space-2)]">
                  <span className="flex flex-wrap gap-[var(--space-2)]" role="radiogroup" aria-label="ประเภทปลายทาง">
                    {([
                      ['none', 'กดไม่ได้'],
                      ['app', 'หน้าในแอป'],
                      ['external', 'เว็บไซต์ภายนอก'],
                    ] as Array<[LinkKind, string]>).map(([k, text]) => (
                      <label
                        key={k}
                        className={`flex cursor-pointer items-center gap-[var(--space-15)] rounded-[var(--radius-pill)] border px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-form)] ${
                          linkKind === k ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]' : 'border-[var(--line-300)] text-[var(--ink-700)]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="banner-link-kind"
                          checked={linkKind === k}
                          onChange={() => {
                            setLinkKind(k);
                            /* เปลี่ยนประเภทแล้วค่าเดิมใช้ต่อไม่ได้ — path ในแอปไม่ใช่ URL และกลับกัน */
                            editField({ linkTo: k === 'none' ? '' : k === 'external' ? 'https://' : '/' });
                          }}
                          className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        />
                        {text}
                      </label>
                    ))}
                  </span>
                  {linkKind !== 'none' && (
                    <input
                      id="banner-link"
                      type={linkKind === 'external' ? 'url' : 'text'}
                      maxLength={LINK_MAX}
                      value={edit.linkTo}
                      onChange={(e) => editField({ linkTo: e.target.value })}
                      className={`${inputCls} bo-num w-full`}
                      placeholder={linkKind === 'external' ? 'https://www.innobic.com/campaign' : '/rewards'}
                    />
                  )}
                  <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    {linkKind === 'none' && 'การ์ดนี้กดไม่ได้ — เหมาะกับแบนเนอร์ประกาศที่ไม่มีหน้าต่อ'}
                    {linkKind === 'app' && 'path ในแอป เช่น /rewards หรือ /privileges'}
                    {linkKind === 'external' && (hostOf(edit.linkTo) && !externalLinkProblem(edit.linkTo)
                      ? `ลูกค้ากดแล้วจะออกจากแอปไปที่ ${hostOf(edit.linkTo)} ในเบราว์เซอร์ — ตรวจให้แน่ใจว่าเป็นเว็บไซต์ที่ถูกต้อง`
                      : 'ต้องขึ้นต้นด้วย https:// — ลูกค้ากดแล้วจะออกจากแอปไปเปิดในเบราว์เซอร์')}
                  </span>
                </span>
              </FormRow>

              {edit.impressions > 0 && (
                <div className="grid grid-cols-3 gap-[var(--space-3)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
                  <Field label="เห็น">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{edit.impressions.toLocaleString('th-TH')} ครั้ง</span>
                  </Field>
                  <Field label="กด">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{edit.taps.toLocaleString('th-TH')} ครั้ง</span>
                  </Field>
                  <Field label="อัตราการกด" hint={(ctr(edit) ?? 0) < 3 ? 'ต่ำกว่าค่าเฉลี่ยของแบนเนอร์อื่น' : undefined}>
                    <span className="bo-num" style={{ textAlign: 'left' }}>{(ctr(edit) ?? 0).toFixed(1)}%</span>
                  </Field>
                </div>
              )}

              {showErrors && editProblems.length > 0 && (
                <Callout tone="error" title={`ยังปิดหน้าต่างนี้ไม่ได้ — แก้อีก ${editProblems.length.toLocaleString('th-TH')} จุด`}>
                  <ul className="m-0 pl-[var(--space-4)]">
                    {editProblems.map((p) => <li key={p.field}>{p.message}</li>)}
                  </ul>
                </Callout>
              )}

              <div className="flex flex-wrap items-center gap-[var(--space-2)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
                <button type="button" onClick={finishEdit} className={primaryBtnCls}>
                  เสร็จแล้ว
                </button>
                <button
                  type="button"
                  disabled={editStatus === 'live'}
                  onClick={() => {
                    setDraft((prev) => prev.filter((x) => x.id !== edit.id).map((b, k) => ({ ...b, order: k + 1 })));
                    setEdit(null);
                  }}
                  className={ghostBtnCls}
                  style={editStatus === 'live' ? undefined : { color: 'var(--error-text)' }}
                >
                  <Icon name="delete" size={16} />
                  ลบแบนเนอร์นี้
                </button>
                {editStatus === 'live' && (
                  <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                    กำลังแสดงอยู่ — ตั้งเป็นฉบับร่างก่อนจึงลบได้
                  </span>
                )}
                <span className="flex-1" />
                <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                  การแก้ไขจะถูกบันทึกเมื่อกด &quot;บันทึกการเปลี่ยนแปลง&quot; ที่แถบด้านล่าง
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        tone={liveCount > 0 ? 'danger' : 'default'}
        title="บันทึกแบนเนอร์หน้าแรก"
        description={
          <>
            <p className="m-0 mb-[var(--space-2)]">
              แก้ไป {changes.length.toLocaleString('th-TH')} รายการ — ลำดับและสถานะใหม่ขึ้นบนหน้าแรกของแอปทันที
            </p>
            <p className="m-0 font-[var(--weight-medium)] text-[var(--ink-900)]">
              จะมีแบนเนอร์แสดงอยู่ {liveCount} ใบ
              {overLimit ? ` — เกินจำนวนที่ลูกค้าเลื่อนดูจริง (${BANNER_VISIBLE_LIMIT} ใบ)` : ''}
            </p>
          </>
        }
        confirmLabel="บันทึกและขึ้นหน้าแรก"
        requireReason
        reasonLabel="เหตุผลที่แก้"
        reasonPresets={[
          'จัดลำดับตามแผนแคมเปญเดือนนี้',
          'หยุดแบนเนอร์ที่แคมเปญสิ้นสุดแล้ว',
          'ขึ้นแบนเนอร์ใหม่ตามที่ฝ่ายการตลาดขอ',
        ]}
        busy={saving}
        onConfirm={(reason) => { void doSave(reason); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default BannerScreen;

/* ============================================================================
   รองรับ:
   FR-HOME-06  ปรับเนื้อหา/แบนเนอร์บนหน้าแรกแบบ Dynamic โดย Admin และแสดงต่างกันตามระดับสมาชิก
   FR-ADM-19   สร้าง/แก้ไข/เผยแพร่/หยุดแคมเปญได้เองโดยไม่ต้องเขียนโปรแกรม
   FR-ADM-37   การสร้าง แก้ไข จัดลำดับ และลบทุกครั้งเข้า Audit Log (backend บันทึกเอง)

   ประเด็นค้าง (⛔):
   — จำนวนแบนเนอร์ที่แสดงพร้อมกันได้จริง (ตอนนี้ใช้ 5 ใบเป็นเกณฑ์เตือน)
     ต้องยืนยันกับข้อมูลการเลื่อนจริงหลังเปิดใช้ 1 เดือน
   — กลุ่มผู้รับเลือกได้แค่ระดับสมาชิก — กลุ่มลูกค้าแบบละเอียดรอระบบ Segment (FR-ADM-25)
   — ยังไม่มีการตั้งเวลาแสดงเป็นชั่วโมง (เช่น แบนเนอร์ Flash Sale 18:00–21:00)
     backend เก็บเป็นวันเวลาแล้ว แต่หน้าจอเลือกเป็นวัน
   ============================================================================ */
